import { getProethicaModuleNumber } from "@constants/demo/proethica-tests";
import { events } from "@constants/tracking";
import { getProethicaModuleTags } from "@features/dashboard/helpers/proethica-module-list";
import useAnalytics from "@hooks/useAnalytics";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useRouter } from "expo-router";
import type { Action } from "kbar";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { EXAM_STATUS } from "types/tag";
import { useProethicaModuleList } from "./useProethicaModuleList";

const statusIconMap: Record<EXAM_STATUS, string> = {
	NOT_STARTED: "play-circle-outline",
	ACTIVE: "play-pause",
	COMPLETE: "file-chart-outline",
	LEGACYREPORT: "file-chart-outline",
	MIGRATEDREPORT: "file-chart-outline",
};

const joinMeta = (parts: (string | undefined)[]) =>
	parts.filter(Boolean).join(" · ");

const NAVIGATE_PRIORITY = -100;

type Options = {
	onOpenQuickStudy?: () => void;
	onShowPurchase?: () => void;
};

export const useProethicaCommandBarActions = ({
	onOpenQuickStudy,
	onShowPurchase,
}: Options = {}) => {
	const router = useRouter();
	const { t } = useTranslation();
	const { data: mixedItemDist } = useMixedItemDist();
	const { resolveModuleItems, getModuleListItemProps, startModule, hasAccess } =
		useProethicaModuleList();
	const { explicitCallTrackCustomEvent } = useAnalytics();

	// Ordered modules with live status and curriculum gating. When the user has
	// access, disabled entries are omitted so only runnable commands surface.
	const entries = useMemo(() => {
		if (!hasAccess) {
			return [];
		}
		const orderedModules = [
			...getProethicaModuleTags(mixedItemDist?.exams),
		].sort(
			(a, b) =>
				(getProethicaModuleNumber(a) ?? 0) - (getProethicaModuleNumber(b) ?? 0),
		);
		return resolveModuleItems(orderedModules)
			.map((tag) => getModuleListItemProps(tag, t))
			.filter((entry) => !entry.disabled);
	}, [
		hasAccess,
		mixedItemDist?.exams,
		resolveModuleItems,
		getModuleListItemProps,
		t,
	]);

	return useMemo(() => {
		const trackOption = (option: string) => {
			explicitCallTrackCustomEvent({
				eventName: events.commandBarOptionClicked,
				option,
			});
		};

		const suggested: Action[] = [];

		if (!hasAccess) {
			suggested.push({
				id: "premium",
				name: t("commandBar.actions.premium"),
				section: t("commandBar.sections.suggested"),
				icon: "crown-outline",
				keywords: "premium unlock purchase",
				perform: () => {
					trackOption("premium");
					onShowPurchase?.();
				},
			});
		}

		const activeEntry = entries.find((entry) => entry.status === "ACTIVE");
		const nextNotStarted = entries.find(
			(entry) => entry.status === "NOT_STARTED",
		);

		if (hasAccess) {
			if (activeEntry) {
				suggested.push({
					id: `resume-${activeEntry.id}`,
					name: t("commandBar.actions.resumeModule", {
						module: activeEntry.title,
					}),
					subtitle: joinMeta([
						activeEntry.typeLabel,
						activeEntry.durationLabel,
					]),
					section: t("commandBar.sections.suggested"),
					icon: "play-pause",
					keywords: `resume ${activeEntry.title} ${activeEntry.subtitle}`,
					perform: () => {
						trackOption(`resume-${activeEntry.id}`);
						startModule(activeEntry.id);
					},
				});
			}

			if (nextNotStarted && nextNotStarted.id !== activeEntry?.id) {
				suggested.push({
					id: `start-${nextNotStarted.id}`,
					name: t("commandBar.actions.startModule", {
						module: nextNotStarted.title,
					}),
					subtitle: nextNotStarted.subtitle,
					section: t("commandBar.sections.suggested"),
					icon: "play-circle-outline",
					keywords: `start ${nextNotStarted.title} ${nextNotStarted.subtitle}`,
					perform: () => {
						trackOption(`start-${nextNotStarted.id}`);
						startModule(nextNotStarted.id);
					},
				});
			}

			suggested.push({
				id: "quick-study",
				name: t("commandBar.actions.quickStudy"),
				subtitle: t("commandBar.actions.quickStudySubtitle"),
				section: t("commandBar.sections.suggested"),
				icon: "play-outline",
				keywords: "quick study practice session",
				perform: () => {
					trackOption("quick-study");
					onOpenQuickStudy?.();
				},
			});
		}

		suggested.push({
			id: "view-progress",
			name: t("commandBar.actions.viewProgress"),
			subtitle: t("commandBar.actions.viewProgressSubtitle"),
			section: t("commandBar.sections.suggested"),
			icon: "chart-timeline-variant-shimmer",
			keywords: "progress mastery stats",
			perform: () => {
				trackOption("view-progress");
				router.push("/(protected)/(tabs)/progress");
			},
		});

		const moduleActions: Action[] = entries.map((entry) => ({
			id: `module-${entry.id}`,
			name: entry.title,
			subtitle: joinMeta([entry.cta, entry.typeLabel, entry.durationLabel]),
			section: t("commandBar.sections.modules"),
			icon: statusIconMap[entry.status],
			keywords: `${entry.title} ${entry.subtitle} ${entry.typeLabel} ${entry.cta}`,
			perform: () => {
				trackOption(`module-${entry.id}`);
				startModule(entry.id);
			},
		}));

		const navigate: Action[] = [
			{
				id: "modules-page",
				name: t("commandBar.actions.allModules"),
				subtitle: t("commandBar.actions.allModulesSubtitle"),
				section: t("commandBar.sections.navigate"),
				icon: "view-grid-outline",
				keywords: "modules list browse",
				perform: () => {
					trackOption("modules-page");
					router.push("/(protected)/(tabs)/modules");
				},
			},
			{
				id: "principles-page",
				name: t("commandBar.actions.principles"),
				subtitle: t("commandBar.actions.principlesSubtitle"),
				section: t("commandBar.sections.navigate"),
				icon: "book-open-page-variant-outline",
				keywords: "principles proethica ethics guide reference",
				perform: () => {
					trackOption("principles-page");
					router.push("/(protected)/(tabs)/principles");
				},
			},
			{
				id: "home",
				name: t("commandBar.actions.home"),
				section: t("commandBar.sections.navigate"),
				icon: "home-outline",
				keywords: "home dashboard",
				perform: () => {
					trackOption("home");
					router.push("/(protected)/(tabs)/home");
				},
			},
			{
				id: "leaderboard",
				name: t("commandBar.actions.leaderboard"),
				section: t("commandBar.sections.navigate"),
				icon: "trophy-outline",
				keywords: "leaderboard leaders rank",
				perform: () => {
					trackOption("leaderboard");
					router.push("/(protected)/(tabs)/leaderboard");
				},
			},
			{
				id: "settings",
				name: t("commandBar.actions.settings"),
				section: t("commandBar.sections.navigate"),
				icon: "cog-outline",
				keywords: "settings account",
				perform: () => {
					trackOption("settings");
					router.push("/account/settings");
				},
			},
		];

		return [
			...suggested,
			...moduleActions,
			...navigate.map((action) => ({
				...action,
				priority: NAVIGATE_PRIORITY,
			})),
		];
	}, [
		entries,
		hasAccess,
		t,
		router,
		startModule,
		onOpenQuickStudy,
		onShowPurchase,
		explicitCallTrackCustomEvent,
	]);
};
